import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GoogleLogo, EnvelopeSimple, Lock, ArrowLeft, ArrowRight,
  User, MagnifyingGlass, CheckCircle, Globe,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

/**
 * LoginPage — PJR B2B agency portal welcome + auth flow.
 *
 * Reference design: Claude's `pjr-b2b.html` mock (Apr 2026).
 * View split:
 *   · `welcome` — full marketing page (hero auth card + 4 feature
 *      cards + stats band + 3-step how-it-works + CTA + footer).
 *   · `form`    — credential form rendered inside the same page
 *      structure (no route change) so the user stays oriented.
 *
 * Assets (already shipped under /public):
 *   /pjr-logo.png              — brand wordmark (used on navbar + footer)
 *   /pjr-isotipo.png           — brand isotype (used inside the auth card)
 *   /pjr-b2b-feat-1..4.png     — 4 editorial photos for the feature cards
 * Hero background keeps the Unsplash photo that the customer explicitly
 * approved ("me gusta la foto que colocaste en el hero, no la cambies").
 */
export default function LoginPage() {
  const { t, language } = useLanguage();
  const es = language === 'es';
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialView = location.state?.preferForm ? 'form' : 'welcome';
  const [view, setView] = useState(initialView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (() => {
    try {
      const saved = sessionStorage.getItem('pjr:postLoginRedirect');
      if (saved) return saved;
    } catch { /* sessionStorage may be blocked */ }
    return location.state?.from?.pathname || '/';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      toast.success(es ? '¡Bienvenido de nuevo!' : 'Welcome back!');
      try { sessionStorage.removeItem('pjr:postLoginRedirect'); } catch { /* noop */ }
      // B2B agency users (master/agent) land on the unified dashboard by
      // default unless a specific `from` redirect was queued (e.g. after
      // visiting a protected route directly). Tester reported masters
      // were stuck on '/'.
      let dest = from;
      if ((!from || from === '/') && result.data?.user_id) {
        try {
          const me = await import('axios').then((m) => m.default.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
            { withCredentials: true }
          ));
          if (me.data?.agency_id && me.data?.agency_role) {
            dest = '/dashboard';
          }
        } catch { /* keep default */ }
      }
      navigate(dest, { replace: true });
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="login-page">
      {/* Fixed navbar — dark translucent, sits over the hero */}
      <nav className="absolute top-0 left-0 right-0 z-[100] h-[70px] flex items-center justify-between px-6 md:px-[52px] bg-pjr-text/[0.82] backdrop-blur-xl border-b border-white/5">
        <Link to="/" className="flex items-center" data-testid="login-nav-logo">
          <img src="/pjr-logo.png" alt="PJR Travel"
                className="h-8 w-auto object-contain [filter:brightness(0)_invert(1)]" draggable="false" />
          <span className="ml-3 px-2 py-[3px] rounded-[5px] bg-pjr-gold text-black text-[10px] font-extrabold uppercase tracking-wider">B2B</span>
        </Link>
        <div className="flex items-center gap-5 sm:gap-7">
          <a href="#features" className="hidden md:inline text-[13px] font-medium text-white/70 hover:text-white transition-colors">
            {es ? 'Productos' : 'Products'}
          </a>
          <a href="#how" className="hidden md:inline text-[13px] font-medium text-white/70 hover:text-white transition-colors">
            {es ? 'Cómo funciona' : 'How it works'}
          </a>
          <a href="#footer" className="hidden md:inline text-[13px] font-medium text-white/70 hover:text-white transition-colors">
            {es ? 'Soporte' : 'Support'}
          </a>
          <button type="button" onClick={() => setView('form')}
            className="bg-pjr-blue hover:bg-[#1a4d8a] text-white text-[13px] font-semibold rounded-md px-5 py-[9px] transition-colors"
            data-testid="nav-login-btn">
            {es ? 'Iniciar sesión' : 'Sign in'}
          </button>
        </div>
      </nav>

      <AnimatePresence mode="wait">
        {view === 'welcome' ? (
          <WelcomeView key="w" es={es} onSignIn={() => setView('form')} navigate={navigate} />
        ) : (
          <FormView
            key="f" es={es}
            onBack={() => setView('welcome')}
            onSubmit={handleSubmit}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            loading={loading} t={t}
            loginWithGoogle={loginWithGoogle}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Welcome view ─────────────────────────────────────────────── */

// Hero photo approved by the customer (editorial travel photo from Unsplash).
const HERO_IMAGE = 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&w=1900&q=80';

function WelcomeView({ es, onSignIn, navigate }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* HERO */}
      <section className="relative h-[580px] flex items-center justify-center overflow-hidden bg-pjr-text">
        {/* Background photo with multi-stop overlay (matches the mock). */}
        <div className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(22,22,23,0.45) 0%, rgba(22,22,23,0.15) 40%, rgba(22,22,23,0.65) 100%), url(${HERO_IMAGE})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }} />

        {/* Silhouette landscape layers (bottom) */}
        <svg className="absolute bottom-0 left-0 right-0 z-[2] pointer-events-none" viewBox="0 0 1440 220" preserveAspectRatio="none">
          <path d="M0,220 L0,130 Q100,50 220,100 Q340,150 440,70 Q540,-10 640,60 Q740,130 840,50 Q940,-30 1040,50 Q1140,130 1240,80 Q1340,30 1440,90 L1440,220 Z" fill="rgba(22,22,23,.5)" />
          <path d="M0,220 L0,170 Q180,110 360,155 Q540,200 720,145 Q900,90 1080,155 Q1260,220 1440,170 L1440,220 Z" fill="rgba(22,22,23,.72)" />
          <path d="M0,220 L0,200 Q360,175 720,195 Q1080,215 1440,198 L1440,220 Z" fill="rgba(22,22,23,.88)" />
        </svg>

        {/* Auth card */}
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative z-10 pt-[70px]"
        >
          <div className="bg-white/[0.97] rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.32),0_4px_16px_rgba(0,0,0,0.12)] px-8 sm:px-[52px] py-10 sm:py-11 w-[440px] max-w-[calc(100vw-32px)] text-center" data-testid="welcome-card">
            {/* Isotipo PJR — official brand mark shipped by the customer. */}
            <img
              src="/pjr-isotipo.png"
              alt="PJR"
              draggable="false"
              className="w-[72px] h-[72px] rounded-[18px] mx-auto mb-[22px] object-cover shadow-[0_8px_28px_rgba(36,103,176,0.38)]"
              data-testid="welcome-isotipo"
            />
            <h1 className="text-[22px] font-extrabold italic uppercase text-pjr-text leading-[1.15] tracking-[-0.01em]" data-testid="welcome-title">
              {es ? 'Bienvenido a PJR B2B' : 'Welcome to PJR B2B'}
            </h1>
            <p className="text-[14px] text-[#7a8fa8] mt-2.5 leading-[1.55]">
              {es
                ? <>Accede a tu cuenta o regístrate para<br />comenzar a reservar con PJR.</>
                : <>Sign in to your account or register to<br />start booking with PJR.</>}
            </p>
            <div className="mt-7 flex gap-3 justify-center flex-col sm:flex-row">
              <button type="button" onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center px-[30px] py-[13px] rounded-[7px] border-2 border-pjr-blue bg-transparent text-pjr-blue font-bold text-[14px] hover:bg-pjr-blue hover:text-white transition-colors"
                data-testid="welcome-register-btn">
                {es ? 'Registrarme' : 'Join now'}
              </button>
              <button type="button" onClick={onSignIn}
                className="relative inline-flex items-center justify-center px-[30px] py-[13px] rounded-[7px] border-2 border-transparent text-white font-bold text-[14px] transition-all hover:-translate-y-[1px] hover:shadow-[0_6px_24px_rgba(36,103,176,0.45)]"
                style={{ background: 'linear-gradient(135deg,#2467B0 0%,#1a4d8a 100%)', boxShadow: '0 4px 16px rgba(36,103,176,0.38)' }}
                data-testid="welcome-signin-btn">
                {es ? 'Iniciar sesión' : 'Sign in'}
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-white py-20 px-6 md:px-[52px]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] sm:text-[30px] font-extrabold italic uppercase tracking-[-0.02em] leading-[1.15] mb-10 sm:mb-[52px]" data-testid="features-title">
            {es ? <>Impulsa tu agencia con <span className="text-pjr-blue">PJR International Travel</span></>
                : <>Boost your agency with <span className="text-pjr-blue">PJR International Travel</span></>}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
            <FeatCard img="/pjr-b2b-feat-1.png" title={es ? 'Oferta global de viajes' : 'Global travel inventory'}
              body={es ? 'Circuitos, hoteles, traslados y experiencias en múltiples destinos alrededor del mundo.'
                       : 'Tours, hotels, transfers and experiences across multiple destinations worldwide.'}
              testId="feat-card-1" />
            <FeatCard img="/pjr-b2b-feat-2.png" title={es ? 'Tarifas para agencias' : 'Agency-only fares'}
              body={es ? 'Opciones competitivas y productos diseñados para que tu agencia venda más y mejor.'
                       : 'Competitive rates and products designed so your agency sells more and better.'}
              testId="feat-card-2" />
            <FeatCard img="/pjr-b2b-feat-3.png" title={es ? 'Reservas simples y rápidas' : 'Simple, fast bookings'}
              body={es ? 'Cotiza, reserva y gestiona tus solicitudes desde una sola plataforma integrada.'
                       : 'Quote, book and manage requests from a single integrated platform.'}
              testId="feat-card-3" />
            <FeatCard img="/pjr-b2b-feat-4.png" title={es ? 'Soporte que te acompaña' : 'Support that has your back'}
              body={es ? 'Equipo especializado disponible para resolver tus dudas y ayudarte a cerrar más reservas.'
                       : 'Specialized team ready to answer questions and help you close more bookings.'}
              testId="feat-card-4" />
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="relative bg-pjr-text px-6 md:px-[52px] py-16 overflow-hidden">
        {/* Gold accent stripe on top */}
        <div className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: 'linear-gradient(90deg, transparent, #c9a84c 30%, #e8cc80 50%, #c9a84c 70%, transparent)' }} />
        {/* Soft blue halo in the top-right corner */}
        <div className="absolute -top-[120px] -right-[80px] w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(36,103,176,0.15) 0%, transparent 70%)' }} />

        <div className="relative z-[1] max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-[1.4fr_auto_1fr_auto_1fr_auto_1fr] gap-y-8 md:gap-x-[52px] items-center">
          <div className="stats-cta">
            <h2 className="text-[24px] sm:text-[26px] font-extrabold italic uppercase text-white tracking-[-0.02em] leading-[1.2] mb-2">
              {es ? 'El tour operador global que impulsa tu agencia' : 'The global tour operator that empowers your agency'}
            </h2>
            <p className="text-[14px] text-white/50 leading-[1.5]">
              {es ? 'Líderes en EE.UU. para el mercado hispano, con alcance global para agencias en cualquier idioma.'
                  : 'Leaders in the US Hispanic market, with global reach for agencies in any language.'}
            </p>
          </div>
          <Separator />
          <StatItem num="+200" label={es ? 'Destinos en todo el mundo' : 'Destinations worldwide'} />
          <Separator />
          <StatItem num="+25" label={es ? <>Años de experiencia<br />en turismo internacional</>
                                            : <>Years of international<br />travel expertise</>} />
          <Separator />
          <StatItem num="24/7" label={es ? 'Soporte para agencias' : 'Agency support'} />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-6 md:px-[52px] py-20">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-pjr-gold mb-3" data-testid="how-eyebrow">
            {es ? '¿Cómo funciona?' : 'How it works'}
          </div>
          <h2 className="text-[28px] sm:text-[30px] font-extrabold italic uppercase tracking-[-0.02em] leading-[1.15] mb-[52px]">
            {es ? <>Tres pasos para <span className="text-pjr-blue">empezar a reservar</span></>
                : <>Three steps to <span className="text-pjr-blue">start booking</span></>}
          </h2>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-11">
            {/* Soft blue connector line between step icons (desktop only) */}
            <div className="hidden md:block absolute top-[31px] left-[calc(16.66%+20px)] right-[calc(16.66%+20px)] h-px opacity-20"
                  style={{ background: 'linear-gradient(90deg,#2467B0 0%,#86C3EA 50%,#2467B0 100%)' }} />
            <Step icon={User} title={es ? 'Crea tu cuenta de agencia' : 'Create your agency account'}
              body={es ? 'Regístrate en minutos. Validamos tu perfil y activamos tu acceso al portal B2B de inmediato.'
                       : 'Register in minutes. We validate your profile and activate your B2B access right away.'}
              testId="step-1" />
            <Step icon={MagnifyingGlass} title={es ? 'Explora y cotiza' : 'Explore & quote'}
              body={es ? 'Accede al catálogo completo: circuitos, hoteles, traslados y experiencias con tarifas netas.'
                       : 'Access the full catalog: tours, hotels, transfers and experiences with net rates.'}
              testId="step-2" />
            <Step icon={CheckCircle} title={es ? 'Confirma y gestiona' : 'Confirm & manage'}
              body={es ? 'Reserva al instante, confirmaciones en tiempo real. Gestiona todos tus expedientes en un solo lugar.'
                       : 'Book instantly, real-time confirmations. Manage all your files in one place.'}
              testId="step-3" />
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative bg-pjr-text text-white text-center px-6 py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20"
              style={{ background: 'radial-gradient(circle at 30% 20%, rgba(36,103,176,0.3) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(201,168,76,0.2) 0%, transparent 40%)' }} />
        <div className="relative z-[1] max-w-2xl mx-auto">
          <h2 className="text-[26px] sm:text-[32px] font-extrabold italic uppercase leading-[1.15]">
            {es ? '¿Listo para crecer con PJR?' : 'Ready to grow with PJR?'}
          </h2>
          <p className="text-[15px] text-white/60 mt-3">
            {es ? 'Únete gratis. Sin compromisos. Empieza a vender desde hoy.'
                : 'Join free. No commitments. Start selling today.'}
          </p>
          <button type="button" onClick={() => navigate('/register')}
            className="mt-7 inline-flex items-center gap-2 bg-white text-pjr-text font-bold text-[14px] px-7 py-3.5 rounded-[10px] hover:-translate-y-[1px] hover:shadow-[0_8px_24px_rgba(255,255,255,0.2)] transition-all"
            data-testid="cta-create-account">
            {es ? 'Crear mi cuenta gratis' : 'Create my free account'}
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="footer" className="bg-pjr-text py-12 px-6 md:px-[52px] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
          <Link to="/" className="flex items-center">
            <img src="/pjr-logo.png" alt="PJR Travel"
                  className="h-7 w-auto object-contain [filter:brightness(0)_invert(1)]" draggable="false" />
          </Link>
          <div className="flex gap-7 flex-wrap justify-center">
            {[
              es ? 'Términos y condiciones' : 'Terms',
              es ? 'Aviso de privacidad' : 'Privacy',
              es ? 'Soporte' : 'Support',
              es ? 'Contacto' : 'Contact',
            ].map((l, i) => (
              <a key={i} href="#" className="text-[13px] text-white/45 hover:text-white/85 transition-colors">{l}</a>
            ))}
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto mt-6 text-[12px] text-white/25">
          © {new Date().getFullYear()} PJR International Travel. {es ? 'Todos los derechos reservados.' : 'All rights reserved.'}
        </div>
      </footer>
    </motion.div>
  );
}

function FeatCard({ img, title, body, testId }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.35 }}
      className="flex flex-col group" data-testid={testId}
    >
      <div
        className="w-full aspect-[4/3] rounded-xl overflow-hidden mb-[18px] relative bg-cover bg-center transition-transform duration-300 group-hover:-translate-y-1"
        style={{ backgroundImage: `url(${img})` }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(22,22,23,0.45) 100%)' }} />
      </div>
      <h3 className="text-[15px] font-bold leading-[1.3] tracking-[-0.01em] mb-[7px]">{title}</h3>
      <p className="text-[13px] text-[#7a8fa8] leading-[1.65] font-normal">{body}</p>
    </motion.article>
  );
}

function Separator() {
  return <div className="hidden md:block w-px h-14 bg-white/10 self-center" />;
}

function StatItem({ num, label }) {
  return (
    <div className="text-center">
      <div className="text-[36px] sm:text-[40px] font-extrabold italic text-[#e8cc80] tracking-[-0.04em] leading-none">{num}</div>
      <div className="text-[12.5px] text-white/50 font-normal mt-2 leading-[1.4]">{label}</div>
    </div>
  );
}

function Step({ icon: Icon, title, body, testId }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.35 }}
                data-testid={testId}>
      <div className="w-[62px] h-[62px] rounded-full border-2 flex items-center justify-center mb-[22px] relative z-[1]"
            style={{
              background: 'linear-gradient(135deg,#2467B0 0%,#1a4d8a 100%)',
              borderColor: 'rgba(201,168,76,0.3)',
              boxShadow: '0 6px 24px rgba(36,103,176,.3), 0 0 0 4px rgba(201,168,76,0.08)',
            }}>
        <Icon size={26} weight="regular" className="text-white" />
      </div>
      <h3 className="text-[16px] font-bold leading-[1.3] mb-1.5">{title}</h3>
      <p className="text-[13.5px] text-[#7a8fa8] leading-[1.65]">{body}</p>
    </motion.div>
  );
}

/* ── Form view (existing login UX, restyled to match the welcome) ───── */

function FormView({ es, onBack, onSubmit, email, setEmail, password, setPassword, loading, t, loginWithGoogle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
      className="min-h-[calc(100vh-70px)] pt-[70px] flex items-center justify-center px-6 py-12 bg-pjr-bg"
      data-testid="login-form-view"
    >
      <div className="w-full max-w-[460px]">
        <button type="button" onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-pjr-blue hover:underline mb-6"
          data-testid="form-back-btn">
          <ArrowLeft size={14} weight="bold" /> {es ? 'Volver' : 'Back'}
        </button>
        <div className="bg-white rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.10)] border border-border/60 p-9">
          <h1 className="text-[24px] font-extrabold italic uppercase text-pjr-text leading-[1.15]">
            {es ? 'Bienvenido' : 'Welcome'}
          </h1>
          <p className="text-[#7a8fa8] mt-2 text-sm">
            {es ? 'Inicia sesión para acceder a tus reservas' : 'Sign in to access your bookings'}
          </p>
          <form onSubmit={onSubmit} className="space-y-5 mt-7" data-testid="login-form">
            <div>
              <Label className="label-text">{t('auth.email')}</Label>
              <div className="relative mt-2">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-11" placeholder="you@example.com" required data-testid="login-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-text">{t('auth.password')}</Label>
                <Link to="/forgot-password"
                      className="text-xs text-pjr-blue hover:underline font-semibold"
                      data-testid="login-forgot-password">
                  {es ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-11" placeholder="••••••••" required data-testid="login-password" />
              </div>
            </div>
            <Button type="submit" className="btn-pjr w-full h-11" disabled={loading} data-testid="login-submit">
              {loading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full h-11" onClick={loginWithGoogle} data-testid="google-login">
            <GoogleLogo size={18} className="mr-2" weight="bold" />
            {t('auth.googleLogin')}
          </Button>
          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-pjr-blue hover:underline font-semibold">{t('auth.register')}</Link>
          </p>
        </div>
      </div>
    </motion.div>
  );
}
